export interface CacheOptions {
  maxAge?: number;
  useSessionStorage?: boolean;
}

export class Cache {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cache = new Map<string, { value: any; timestamp: number; maxAge: number }>();
  private sessionStorageKeys = new Set<string>();
  private maxSize: number;
  private defaultMaxAge: number; // in milliseconds

  constructor(maxSize: number, defaultMaxAge: number) {
    this.maxSize = maxSize;
    this.defaultMaxAge = defaultMaxAge;
  }

  /**
   * Retrieves an item from the cache.
   * @param key The key of the item to retrieve.
   * @returns The cached item or undefined if not found.
   */
  get<T>(key: string): T | undefined {
    let item;
    if (this.cache.has(key)) {
      item = this.cache.get(key);
    } else if (sessionStorage.getItem(key)) {
      // if item was cached, it might not be in our session storage keys yet
      this.sessionStorageKeys.add(key);
      item = JSON.parse(sessionStorage.getItem(key)!);
    }

    if (item) {
      const now = Date.now();
      if (now - item.timestamp > item.maxAge) {
        this.cache.delete(key);
        sessionStorage.removeItem(key);
        this.sessionStorageKeys.delete(key);
        return undefined;
      }
    }
    return item?.value;
  }

  /**
   * Sets an item in the cache.
   * @param key The key of the item to set.
   * @param value The value of the item to set.
   * @param options Optional cache options.
   */
  set<T>(key: string, value: T, options?: CacheOptions): void {
    const maxAge = options?.maxAge ?? this.defaultMaxAge;
    const useSessionStorage = options?.useSessionStorage ?? false;

    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
        sessionStorage.removeItem(oldestKey);
        this.sessionStorageKeys.delete(oldestKey);
      }
    }

    const item = { value, timestamp: Date.now(), maxAge };
    if (useSessionStorage) {
      sessionStorage.setItem(key, JSON.stringify(item));
      this.sessionStorageKeys.add(key);
    } else {
      this.cache.set(key, item);
    }
  }

  /**
   * Clears the cache.
   */
  clear(): void {
    // ensure session storage cleares account details regardless of key
    this.sessionStorageKeys.add('account-details');
    // clean it up
    this.cache.clear();
    this.sessionStorageKeys.forEach((key) => sessionStorage.removeItem(key));
    this.sessionStorageKeys.clear();
  }
}
